import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Siren, Lock, Zap, MapPin, Phone, Plus, Trash2, Send, UserCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import PricingDialog from "@/components/PricingDialog";

interface EmergencyContact {
  name: string;
  phone: string;
  telegramId?: string;
}

const PanicAlerts = ({ embedded }: { embedded?: boolean }) => {
  const [pricingOpen, setPricingOpen] = useState(false);
  const { user, isPremium } = useAuth();
  const [contacts, setContacts] = useState<EmergencyContact[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("panic_contacts") || "[]");
    } catch {
      return [];
    }
  });
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newTelegram, setNewTelegram] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [panicActive, setPanicActive] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    localStorage.setItem("panic_contacts", JSON.stringify(contacts));
  }, [contacts]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => {
      if (countdown === 1) {
        sendPanicAlert();
        setCountdown(0);
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const addContact = () => {
    if (!newName.trim() || (!newPhone.trim() && !newTelegram.trim())) {
      toast.error("Name and at least one contact method required");
      return;
    }
    setContacts((prev) => [...prev, { name: newName, phone: newPhone, telegramId: newTelegram || undefined }]);
    setNewName("");
    setNewPhone("");
    setNewTelegram("");
    setShowAdd(false);
    toast.success("Emergency contact added");
  };

  const removeContact = (index: number) => {
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerPanic = () => {
    if (contacts.length === 0) {
      toast.error("Add at least one emergency contact first");
      return;
    }

    // Get current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => toast.warning("Location unavailable — alert will be sent without coordinates")
      );
    }

    setCountdown(5);
    setPanicActive(true);
    toast.warning("🚨 PANIC ALERT will be sent in 5 seconds! Tap Cancel to abort.");
  };

  const cancelPanic = () => {
    setCountdown(0);
    setPanicActive(false);
    toast.info("Panic alert cancelled");
  };

  const sendPanicAlert = async () => {
    setPanicActive(false);

    const locationStr = location
      ? `https://www.google.com/maps?q=${location.lat},${location.lng}`
      : "Location unavailable";

    const message = `🚨 EMERGENCY PANIC ALERT\n\nI need immediate help!\n📍 Location: ${locationStr}\n🕐 Time: ${new Date().toLocaleString("en-GB")}\n\nThis is an automated emergency alert from Conflict Monitor.`;

    // Send Telegram alerts for contacts with telegram IDs
    const telegramContacts = contacts.filter((c) => c.telegramId);
    if (telegramContacts.length > 0) {
      try {
        for (const contact of telegramContacts) {
          await supabase.functions.invoke("check-alerts", {
            body: { panic: true, chatId: contact.telegramId, message },
          });
        }
      } catch {
        // Telegram send is best-effort
      }
    }

    toast.success(`🚨 Panic alert sent to ${contacts.length} contacts!`, {
      description: `Location: ${location ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` : "unavailable"}`,
      duration: 10000,
    });

    // Also show the message for copy (for SMS/WhatsApp sharing)
    try {
      await navigator.clipboard.writeText(message);
      toast.info("Emergency message copied to clipboard — share via SMS/WhatsApp", { duration: 8000 });
    } catch {
      // Clipboard not available
    }
  };

  if (!isPremium && !embedded) {
    return (
      <section className="border-b border-border">
        <div className="container mx-auto px-4 py-12">
          <div className="relative overflow-hidden rounded-xl border border-border bg-card p-8 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-destructive/5 to-transparent" />
            <div className="relative">
              <Lock className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
              <h2 className="mb-2 text-2xl font-bold text-foreground">🆘 Panic Alert</h2>
              <p className="mb-6 text-muted-foreground max-w-lg mx-auto">
                One-tap emergency alert that shares your GPS location with emergency contacts via Telegram and clipboard.
              </p>
              <Button
                onClick={() => setPricingOpen(true)}
                className="bg-primary text-primary-foreground"
              >
                <Zap className="mr-2 h-4 w-4" /> Unlock Panic Alerts
              </Button>
            </div>
          </div>
        </div>
        <PricingDialog open={pricingOpen} onOpenChange={setPricingOpen} />
      </section>
    );
  }

  const Wrapper = embedded ? "div" : "section";
  const wrapperProps = embedded ? {} : { id: "panic-alert", className: "border-b border-border" };

  return (
    <Wrapper {...wrapperProps}>
      <div className={embedded ? "" : "container mx-auto px-4 py-8"}>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Siren className="h-6 w-6 text-destructive" /> Geo-targeted Panic Alert
          </h2>
          <p className="text-sm text-muted-foreground">
            One-tap emergency SOS — shares your location with your emergency contacts
          </p>
        </div>

        {/* PANIC BUTTON */}
        <div className="mb-8 text-center">
          {countdown > 0 ? (
            <div className="space-y-4">
              <div className="mx-auto h-32 w-32 rounded-full border-4 border-destructive bg-destructive/20 flex items-center justify-center animate-pulse">
                <span className="text-5xl font-black text-destructive">{countdown}</span>
              </div>
              <p className="text-sm text-destructive font-semibold">Sending panic alert in {countdown}s...</p>
              <Button onClick={cancelPanic} variant="outline" size="lg">
                Cancel
              </Button>
            </div>
          ) : (
            <button
              onClick={triggerPanic}
              className="mx-auto flex h-32 w-32 items-center justify-center rounded-full border-4 border-destructive bg-destructive/10 transition-all hover:bg-destructive/30 hover:scale-105 active:scale-95"
            >
              <div className="text-center">
                <Siren className="mx-auto h-10 w-10 text-destructive" />
                <span className="mt-1 block text-xs font-bold uppercase text-destructive">SOS</span>
              </div>
            </button>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            Tap the button to send an emergency alert with your GPS coordinates
          </p>
        </div>

        {/* Emergency Contacts */}
        <div className="rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-primary" /> Emergency Contacts ({contacts.length})
            </h3>
            <Button size="sm" variant="secondary" onClick={() => setShowAdd(!showAdd)}>
              <Plus className="mr-1 h-4 w-4" /> Add
            </Button>
          </div>

          {showAdd && (
            <div className="mb-4 rounded border border-border bg-background p-4 space-y-3">
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Name" className="bg-card" />
              <Input value={newPhone} onChange={(e) => setNewPhone(e.target.value)} placeholder="Phone number" className="bg-card" />
              <Input value={newTelegram} onChange={(e) => setNewTelegram(e.target.value)} placeholder="Telegram Chat ID (optional)" className="bg-card" />
              <Button onClick={addContact} size="sm" className="bg-primary text-primary-foreground">
                Save Contact
              </Button>
            </div>
          )}

          {contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No emergency contacts yet. Add at least one to use the panic button.
            </p>
          ) : (
            <div className="space-y-2">
              {contacts.map((c, i) => (
                <div key={i} className="flex items-center justify-between rounded border border-border/50 bg-background px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">{c.name}</p>
                    <div className="flex gap-2 mt-1">
                      {c.phone && (
                        <Badge variant="outline" className="text-xs font-mono">
                          <Phone className="mr-1 h-3 w-3" /> {c.phone}
                        </Badge>
                      )}
                      {c.telegramId && (
                        <Badge variant="outline" className="text-xs font-mono">
                          <Send className="mr-1 h-3 w-3" /> TG
                        </Badge>
                      )}
                    </div>
                  </div>
                  <button onClick={() => removeContact(i)} className="text-muted-foreground hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default PanicAlerts;
